import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { Button, Input, Alert } from '@/components/ui';
import { api } from '@/lib/api';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await api.forgotPassword(email);
      setIsSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send reset email');
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="verdict-card rounded-2xl p-8 sm:p-10 text-center max-w-md w-full mx-auto">
        <div className="w-16 h-16 bg-success/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-success" />
        </div>
        <h2 className="text-2xl font-bold text-text-primary mb-2">Check Your Email</h2>
        <p className="text-text-secondary mb-6">
          If an account with <strong>{email}</strong> exists, we've sent a password reset link.
          <br />
          <span className="text-sm">The link will expire in 1 hour.</span>
        </p>
        <div className="space-y-3">
          <Button
            variant="secondary"
            className="w-full"
            onClick={() => {
              setIsSubmitted(false);
              setEmail('');
            }}
          >
            Try a different email
          </Button>
          <Link to="/login" className="block">
            <Button variant="ghost" className="w-full" leftIcon={<ArrowLeft className="w-4 h-4" />}>
              Back to Login
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="verdict-card rounded-2xl p-8 sm:p-10 max-w-md w-full mx-auto">
      <h2 className="text-2xl font-bold text-text-primary text-center mb-2">Forgot Password?</h2>
      <p className="text-text-secondary text-center mb-6">
        Enter your email and we'll send you a reset link
      </p>

      {error && (
        <Alert variant="error" className="mb-6" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          name="email"
          type="email"
          label="Email"
          placeholder="john@example.com"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setError(null);
          }}
          leftIcon={<Mail className="w-5 h-5" />}
          required
        />

        <Button type="submit" className="w-full" size="lg" isLoading={isLoading}>
          Send Reset Link
        </Button>
      </form>

      <p className="text-center text-text-muted mt-6">
        <Link to="/login" className="text-verdict-primary hover:text-verdict-primary-hover transition-colors inline-flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" />
          Back to Login
        </Link>
      </p>
    </div>
  );
}

